import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DateInput } from '../../../../shared/components/DateInput.js';

interface Article {
  readonly articleId: string;
  readonly articleCode: string;
  readonly title: string;
  readonly category: string;
  readonly version: number;
}

/** M27 — Knowledge Portal (§31). Browses published articles and runs the
 * published-only advanced search (§17): keyword + category, tag, author,
 * article id, document type, department, region and creation date range. */
export function M27KnowledgePortalPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<Article[]>([]);
  const [total, setTotal] = useState(0);
  const [term, setTerm] = useState('');
  const [filters, setFilters] = useState({
    category: '', tag: '', author: '', articleCode: '', documentType: '', department: '', region: '', dateFrom: '', dateTo: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const setFilter = (key: keyof typeof filters, value: string) =>
    setFilters((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: Article[]; total: number }>('/v1/collab/knowledge?publishedOnly=true', { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  async function search(): Promise<void> {
    const params = new URLSearchParams();
    if (term.trim()) params.set('q', term.trim());
    for (const [k, v] of Object.entries(filters)) if (v.trim()) params.set(k, v.trim());
    try {
      const r = await apiRequest<{ data: Article[] }>(`/v1/collab/knowledge/search?${params.toString()}`, { token });
      setRows(r.data); setTotal(r.data.length); setError(null);
    } catch (e) { setError((e as Error).message); }
  }

    // The API requires at least one criterion (M27 search validation), so the
    // button stays closed until the user has given one.
    const hasCriterion =
      term.trim().length > 0 || Object.values(filters).some((v) => v.trim().length > 0);

    return (
      <main aria-label="knowledge-portal">
      <h1>{t('m27.knowledgePortal.heading')}</h1>
      <label htmlFor="kb-search">{t('m27.knowledgePortal.fldSearch')}</label>
      <input id="kb-search" value={term} onChange={(e) => setTerm(e.target.value)} />

      <fieldset aria-label="advanced-filters">
        <legend>{t('m27.knowledgePortal.advancedFilters')}</legend>
        <label htmlFor="kb-category">{t('m27.knowledgePortal.fldCategory')}</label>
        <input id="kb-category" value={filters.category} onChange={(event) => setFilter('category', event.target.value)} />
        <label htmlFor="kb-documentType">{t('m27.knowledgePortal.fldDocumentType')}</label>
        <input id="kb-documentType" value={filters.documentType} onChange={(event) => setFilter('documentType', event.target.value)} />
        <label htmlFor="kb-tag">{t('m27.knowledgePortal.fldTag')}</label>
        <input id="kb-tag" value={filters.tag} onChange={(event) => setFilter('tag', event.target.value)} />
        <label htmlFor="kb-author">{t('m27.knowledgePortal.fldAuthor')}</label>
        <input id="kb-author" value={filters.author} onChange={(event) => setFilter('author', event.target.value)} />
        <label htmlFor="kb-articleCode">{t('m27.knowledgePortal.fldArticleId')}</label>
        <input id="kb-articleCode" value={filters.articleCode} onChange={(event) => setFilter('articleCode', event.target.value)} />
        <label htmlFor="kb-department">{t('m27.knowledgePortal.fldDepartment')}</label>
        <input id="kb-department" value={filters.department} onChange={(event) => setFilter('department', event.target.value)} />
        <label htmlFor="kb-region">{t('m27.knowledgePortal.fldRegion')}</label>
        <input id="kb-region" value={filters.region} onChange={(event) => setFilter('region', event.target.value)} />
        <label htmlFor="kb-dateFrom">{t('m27.knowledgePortal.fldDateFrom')}</label>
        <DateInput id="kb-dateFrom" value={filters.dateFrom} onValueChange={(value) => setFilter('dateFrom', value)} />
        <label htmlFor="kb-dateTo">{t('m27.knowledgePortal.fldDateTo')}</label>
        <DateInput id="kb-dateTo" value={filters.dateTo} onValueChange={(value) => setFilter('dateTo', value)} />
      </fieldset>

      <button type="button" disabled={!hasCriterion} onClick={() => void search()}>{t('m27.knowledgePortal.btnSearch')}</button>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m27.knowledgePortal.loading')}</p> : (
        <>
          <p aria-label="article-count">{t('m27.knowledgePortal.count', { count: total })}</p>
          <table aria-label="article-table">
            <thead><tr><th>{t('m27.knowledgePortal.colCode')}</th><th>{t('m27.knowledgePortal.colTitle')}</th><th>{t('m27.knowledgePortal.colCategory')}</th><th>{t('m27.knowledgePortal.colVersion')}</th></tr></thead>
            <tbody>{rows.map((a) => <tr key={a.articleId}><td>{a.articleCode}</td><td>{a.title}</td><td>{a.category}</td><td>v{a.version}</td></tr>)}</tbody>
          </table>
        </>
      )}
    </main>
  );
}
